/**
 * Drilldown Modal Component for Supplier Performance
 *
 * The shell only: title, Export, the scroll container and the pager. The
 * columns per type live in ./drilldown (one definition each for the table and
 * the export).
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { PAGE_SIZE_OPTIONS, RowsSelect } from '../../components/ui/pagination';
import { useFormatters } from '../../contexts/PreferencesContext';
import { AlertCircle, Archive, Download, Loader2 } from 'lucide-react';
import { ModalType, POLineMetric } from './types';
import { AnyRow, DrilldownFormatters } from './drilldown/columns';
import { columnsFor } from './drilldown/columnsFor';
import { DrilldownTable } from './drilldown/DrilldownTable';

interface DrilldownModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  subtitle: string;
  type: ModalType;
  /** The cell label the modal opened from ('Reject Value' widens the NCR columns). */
  filterType?: string;
  /** PO-line drills: the cell's population ('qty_diff' adds a Qty Diff column). */
  metric?: POLineMetric;
  /** Supplier viewer: the customer's reporting-currency columns are withheld. */
  isSupplier?: boolean;
  data: unknown[];
  loading: boolean;
  error?: string | null;
  page: number;
  /** CR-8186: rows per page, chosen with the shared Rows control. */
  pageSize: number;
  totalCount: number;
  totalPages: number;
  currency: string;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /** Exports the FULL filtered population (every page) with the table's columns. */
  onExport?: () => void;
  exporting?: boolean;
}

export function DrilldownModal({
  open,
  onOpenChange,
  title,
  subtitle,
  type,
  filterType,
  metric,
  isSupplier = false,
  data,
  loading,
  error,
  page,
  pageSize,
  totalCount,
  totalPages,
  currency,
  onPageChange,
  onPageSizeChange,
  onExport,
  exporting = false,
}: DrilldownModalProps) {
  const { formatDate, formatDateTime, formatCurrency, formatQuantity } = useFormatters();
  const formatters: DrilldownFormatters = {
    formatDate,
    formatDateTime,
    money: (value, code) => formatCurrency(value, code, 2),
    currency,
    // CR-7262 owns the quantity format platform-wide.
    qty: (value) => formatQuantity(value as number | string | null | undefined),
  };
  const rows = data as AnyRow[];
  const columns = columnsFor(type, formatters, { filterType, metric, isSupplier });
  const total = totalCount || data.length;
  // CR-7270: in Historical mode a past month's arrears is a frozen month-end
  // balance, and every value in these rows is as at that boundary. Nothing in a
  // rendered row says so, so the modal does — otherwise the reader assumes the
  // quantities and statuses are current, which is how the original defect was
  // read as data corruption rather than as history.
  const frozenAsAt = rows
    .map((row) => (row as { frozen_as_at?: string | null }).frozen_as_at)
    .find((value): value is string => Boolean(value));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* pr-8 keeps the Export button clear of the dialog's absolute close button. */}
        <DialogHeader className="pr-8">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <DialogTitle>{title}</DialogTitle>
              <DialogDescription>{subtitle}</DialogDescription>
              {frozenAsAt && (
                <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">
                  <Archive className="h-3.5 w-3.5" />
                  Cached month-end data as at {formatDate(frozenAsAt)}
                </span>
              )}
            </div>
            {onExport && (
              <Button
                variant="outline"
                size="sm"
                onClick={onExport}
                disabled={exporting || loading || total === 0}
                title="Export every record in this drill-down to Excel"
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                {exporting ? 'Exporting…' : 'Export'}
              </Button>
            )}
          </div>
        </DialogHeader>

        {/* min-h prevents layout shift during loading that causes visual jerking.
            scrollbar-always: the wide tables scroll sideways here, and a mouse
            user needs the bar drawn to reach the right-hand columns. */}
        <div className="flex-1 min-h-[400px] overflow-y-auto scrollbar-always">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center gap-2 py-8 text-red-600">
              <AlertCircle className="h-4 w-4" />
              <span>{error}</span>
            </div>
          ) : rows.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No records behind this figure for the selected filters.
            </p>
          ) : (
            <DrilldownTable rows={rows} columns={columns} formatters={formatters} />
          )}
        </div>

        {/* Pagination footer. CR-8186: the Rows control shows on a single
            page too, so a size larger than the list can be taken back down. */}
        {!loading && !error && rows.length > 0 && (
          <div className="pt-4 border-t flex items-center justify-between text-sm text-muted-foreground">
            <div className="flex items-center gap-3">
              <RowsSelect value={pageSize} options={PAGE_SIZE_OPTIONS} onChange={onPageSizeChange} />
              <span>
                Showing {((page - 1) * pageSize + 1).toLocaleString()} -{' '}
                {Math.min(page * pageSize, total).toLocaleString()} of{' '}
                {total.toLocaleString()} record{total !== 1 ? 's' : ''}
              </span>
            </div>
            {(totalCount > pageSize || rows.length === pageSize) && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPageChange(page - 1)}
                  disabled={page <= 1 || loading}
                >
                  Previous
                </Button>
                <span className="px-2">
                  Page {page} of {totalPages || '?'}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPageChange(page + 1)}
                  disabled={page >= totalPages || loading}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
